/**
 * Timeline View for Audit Events
 * Visual alternative to list view
 */
import React from 'react';
import { Shield, Hash, Clock, User, FileText } from 'lucide-react';

export function TimelineView({ events, onSelectEvent }) {
  // Group by date
  const eventsByDate = events.reduce((acc, event) => {
    const timestamp = event.event_timestamp || event.eventTimestamp;
    const dateKey = new Date(timestamp).toLocaleDateString();
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(event);
    return acc;
  }, {});

  const dates = Object.keys(eventsByDate).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  const getEventIcon = (eventType) => {
    if (eventType?.includes('upload')) return '📤';
    if (eventType?.includes('approve')) return '✅';
    if (eventType?.includes('reject')) return '❌';
    if (eventType?.includes('update') || eventType?.includes('edit')) return '✏️';
    if (eventType?.includes('delete')) return '🗑️';
    if (eventType?.includes('audit_pack')) return '📦';
    return '📄';
  };

  return (
    <div className="timeline-view space-y-8" data-testid="timeline-view">
      {dates.map(date => (
        <div key={date} className="date-group">
          {/* Date Header */}
          <div className="date-header sticky top-0 z-10 bg-[#0A0A0A] py-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#2F2F2F]" />
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#111111] border-2 border-emerald-500/30 shadow-sm">
                <Clock className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-semibold text-white">{date}</span>
                <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                  {eventsByDate[date].length} events
                </span>
              </div>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#2F2F2F]" />
            </div>
          </div>

          {/* Timeline */}
          <div className="relative pl-8">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-500/50 via-emerald-500/30 to-transparent" />

            {/* Events */}
            <div className="space-y-4">
              {eventsByDate[date].map((event, idx) => {
                const eventType = event.event_type || event.eventType || '';
                const actor = event.actor?.user_name || event.actorUserName || 'System';
                const timestamp = event.event_timestamp || event.eventTimestamp;
                const chainIndex = event.chain_index || event.chainIndex || idx;
                const eventHash = event.event_hash || event.eventHash;
                const aggregateName = event.aggregate_name || event.aggregateName || event.aggregate_id || '';

                return (
                  <div key={event._id || event.event_id || idx} className="relative">
                    {/* Timeline dot */}
                    <div className="absolute -left-5 top-6 h-3 w-3 rounded-full bg-emerald-500 border-4 border-[#0A0A0A] shadow-sm" />

                    {/* Event card */}
                    <div 
                      className="event-card ml-8 p-4 rounded-lg bg-[#111111] border border-[#1F1F1F] hover:border-emerald-500/30 hover:shadow-lg cursor-pointer transition-all"
                      onClick={() => onSelectEvent(event._id || event.event_id)}
                      data-testid={`timeline-event-${event._id || event.event_id || idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-[#6B6B6B]">
                              {new Date(timestamp).toLocaleTimeString()}
                            </span>
                            <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#A0A0A0] text-xs">
                              {eventType}
                            </span>
                            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-mono">
                              #{chainIndex}
                            </span>
                          </div>

                          <p className="text-sm font-semibold text-white mb-1">
                            <span className="mr-2">{getEventIcon(eventType)}</span>
                            {actor} · {eventType.split('.').pop()?.replace(/_/g, ' ')}
                          </p>

                          <p className="text-xs text-[#6B6B6B] mb-2">
                            {aggregateName?.substring(0, 40)}{aggregateName?.length > 40 ? '...' : ''}
                          </p>

                          {/* Hash preview */}
                          {eventHash && (
                            <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
                              <Hash className="h-3 w-3" />
                              <span className="font-mono">
                                {eventHash.substring(0, 12)}...
                              </span>
                            </div>
                          )}
                        </div>

                        <button className="px-3 py-1 rounded text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors">
                          Details →
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {dates.length === 0 && (
        <div className="text-center py-12">
          <Shield className="h-12 w-12 text-[#2F2F2F] mx-auto mb-4" />
          <p className="text-[#6B6B6B]">No events to display</p>
        </div>
      )}
    </div>
  );
}

export default TimelineView;
